import type { ReservationMode } from '@iheartjane/types';
import { useMemo } from 'react';
import type { DeepReadonly } from 'ts-essentials';

import { EMPTY_V3_CART } from '@jane/monolith/components/cart/cartFacade/emptyV3Cart';
import { useCartState } from '@jane/monolith/components/cart/cartFacade/useCartState';
import { minimumDeliveryOrder } from '@jane/monolith/utils/minimumDeliveryOrder';
import { minimumPickupOrder } from '@jane/monolith/utils/minimumPickupOrder';
/* eslint-disable-next-line no-restricted-imports */
import type { Store } from '@jane/shared/models';
import type { CartLineItems, DeliveryValidation } from '@jane/shared/types';
import type { CartLimitPolicyReport } from '@jane/shared/util';
import { generateCartLimitReport } from '@jane/shared/util';

export interface UseValidCartProps {
  cartLineItems: CartLineItems;
  deliveryValidationData: DeliveryValidation | null;
  /**
   * The cart's mode. `'pickup'` and `'curbside'` both take the pickup minimum,
   * which `getPickupMinAmount` resolves from the store's curbside setting.
   */
  reservationMode: ReservationMode;
  store: DeepReadonly<Store>;
}

interface UseValidCartReturn {
  cartReport: CartLimitPolicyReport | null;
  hasValidCart: boolean;
  minimumDeliveryOrderMet: boolean;
  minimumPickupOrderMet: boolean;
}

export const useValidateCart = ({
  cartLineItems,
  deliveryValidationData,
  reservationMode: currentReservationMode,
  store,
}: UseValidCartProps): UseValidCartReturn => {
  const { cart, isFetching, isLoading } = useCartState();

  return useMemo(() => {
    const earlyReturn = {
      cartReport: null,
      hasValidCart: false,
      minimumDeliveryOrderMet: false,
      minimumPickupOrderMet: false,
    };

    const cartReport =
      store && store.cart_limit_policy
        ? generateCartLimitReport({
            cart: cart ?? EMPTY_V3_CART,
            store,
          })
        : null;

    if (isLoading) {
      return earlyReturn;
    }

    const underCartLimit = cartReport ? !cartReport.over : true;

    const minimumPickupOrderMet =
      currentReservationMode === 'pickup' ||
      currentReservationMode === 'curbside'
        ? minimumPickupOrder(cartLineItems.discountedSubtotal, store)
        : true;

    const minimumDeliveryOrderMet =
      deliveryValidationData && currentReservationMode === 'delivery'
        ? minimumDeliveryOrder({
            discountedSubtotal: cartLineItems.discountedSubtotal,
            store,
            deliveryValidateData: deliveryValidationData,
          }) && !!deliveryValidationData?.can_deliver
        : true;

    const hasInvalidAddress =
      currentReservationMode === 'delivery'
        ? !deliveryValidationData?.can_deliver
        : false;

    // A refetch after a write: keep the banners, but don't check out a stale cart.
    const hasValidCart =
      !isFetching &&
      underCartLimit &&
      minimumPickupOrderMet &&
      minimumDeliveryOrderMet &&
      !hasInvalidAddress;

    return {
      cartReport,
      hasValidCart,
      minimumPickupOrderMet,
      minimumDeliveryOrderMet,
    };
  }, [
    cart,
    cartLineItems.discountedSubtotal,
    currentReservationMode,
    deliveryValidationData,
    isFetching,
    isLoading,
    store,
  ]);
};
